import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useWindowWidth } from '../hooks';
import { TEAL, AMBER, RED, BORD, CARD, SUNK, MUTED, DIM, TEXT, KINDS, kindOf, perOneTyped } from '../stockShared';

// Add a stock item, or change one (the stock module, 22 Oct 2026; "Where does
// it go?", 29 Oct 2026). Since 29 Oct: the name finds what is already in Stock
// or in Admin first, so nothing is entered twice ("no data is creating
// duplicate entries, but every entry reads from the same space"); the kinds
// that are an Admin record are linked to it here; and the teal box asks where
// it goes -- a step takes it, a new step in the line, or nothing uses it --
// with whatever + Item was pressed on already picked. Its movements are never
// touched here, but a new item can be counted as it is added.
//
// item: the one being changed, or null for a new one
// start: what + Item was pressed on -- { product_id, product_name, step_id }
//   (a product's group, or a step in Build steps) or { part_item_id, part_name }
//   (a moulded part, for something moulded into it)
// onSaved(item): after saving   onOpenItem(id): open an item already in Stock

// The Admin record each kind is, where it is one.
const LINK_OF = { packaging: 'packaging_id', moulded: 'part_id', material: 'material_id', finished: 'product_id' };
const LIST_OF = { packaging_id: 'packaging', part_id: 'parts', material_id: 'materials', product_id: 'products' };
const WHICH = { packaging: 'The same item in Admin → Packaging', moulded: 'The same part in Parts', material: 'The same material in Materials', finished: 'The same product in Admin → Products' };
// Where a linked item's name lives (093, 5 Nov 2026): one record, so Stock shows it and does not ask.
const NAME_HOME = { packaging: 'Admin → Packaging', moulded: 'Parts', finished: 'Admin → Products' };
const PLACED = ['bought', 'customer', 'made', 'stage', 'moulded'];
const PACK_KINDS = [['transport_box', 'Transport box'], ['folding_box', 'Product box'], ['bag', 'Bag'], ['insert', 'Tray, insert or sheet'], ['pallet', 'Pallet'], ['tape', 'Tape'], ['foil', 'Foil'], ['other', 'Other']];
const unitFor = (type, category) => (type === 'materials' || (type === 'packaging' && category === 'foil') ? 'kg' : type === 'packaging' && category === 'tape' ? 'm' : 'kos');
const KIND_OF_LIST = { parts: 'moulded', products: 'finished', packaging: 'packaging', materials: 'material' };
const words = (s) => new Set(String(s || '').toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(w => w.length > 2));
const LEVEL_SAID = { moulded: 'level 2, Moulded here', bought: 'level 3, Bought in', customer: 'level 3, Bought in', packaging: 'level 3, Bought in', made: 'level 4, Made here', stage: 'level 5, Sub-assemblies (6 if the packed product takes it)' };

export default function StockItemForm({ item = null, start = null, onClose, onSaved, onOpenItem }) {
  const phone = useWindowWidth() < 640;
  const fromPart = !!(start && start.part_item_id);
  const [opts, setOpts] = useState(null);
  const [stock, setStock] = useState(null);          // every Stock item, to find before adding
  const [builds, setBuilds] = useState({});          // product id -> its build
  const [existing, setExisting] = useState(null);    // an item already in Stock, being placed
  const [f, setF] = useState(() => ({
    name: item?.name || '', kind: item?.kind || 'bought', unit: item?.unit || 'kos',
    pack_unit: item?.pack_unit || '', pack_qty: item?.pack_qty ?? '', min_qty: item?.min_qty ?? '',
    chamfer: !!item?.chamfer, note: item?.note || '',
    from_production: item ? item.from_production !== false : true, per_part: item?.per_part != null ? perOneTyped(item.per_part) : '1',
    link_id: item ? (item[LINK_OF[item.kind]] ?? '') : '',
    on_hand: '',
  }));
  const [newPack, setNewPack] = useState(null);      // { name, category } -- a new Packaging item
  const [w, setW] = useState(() => ({
    mode: item ? 'keep' : 'into',
    rows: [{ product_id: fromPart ? '' : (start?.product_id || ''), step_id: fromPart ? String(start.part_item_id) : (start?.step_id ? String(start.step_id) : ''), qty: '1' }],
    from: [], swap: true,
    line: { product_id: start?.product_id || '', after_id: '' },
    also: { on: false, product_id: '', step_id: '', qty: '1' },
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const kind = existing ? existing.kind : f.kind;
  const built = kind === 'made' || kind === 'stage';
  const linkKey = LINK_OF[kind] || null;
  // The pieces of a family mould keep their own names: one part, several in Stock.
  const familyPiece = (partId) => kind === 'moulded' && (stock || []).some(s => s.active !== false && s.id !== item?.id && String(s.part_id) === String(partId));
  const nameFollows = !!NAME_HOME[kind] && !!f.link_id && !newPack && (kind !== 'moulded' || !!stock) && !familyPiece(f.link_id);

  useEffect(() => {
    api.get('/api/v1/stock/options').then(r => setOpts(r.data)).catch(() => setError('Could not read the MouldOS lists. Close and try again.'));
    api.get('/api/v1/stock').then(r => setStock(r.data.items || [])).catch(() => setStock([]));
  }, [item]);

  // A product's build: its steps and lists, read once when it is picked.
  const productIds = [...new Set([...w.rows.map(r => r.product_id), w.line.product_id, w.also.product_id].filter(Boolean))].join(',');
  useEffect(() => {
    for (const id of productIds.split(',').filter(Boolean)) {
      if (builds[id]) continue;
      api.get(`/api/v1/stock/products/${id}/build`).then(r => setBuilds(b => ({ ...b, [id]: r.data }))).catch(() => setError('Could not read that product\'s steps. Close and try again.'));
    }
  }, [productIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF(x => ({ ...x, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const setRow = (i, patch) => setW(x => ({ ...x, rows: x.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const products = (opts?.products || []).filter(p => !p.discontinued);
  const nameOfItem = (b, id) => b?.items?.[id]?.name || 'An item';

  // The steps of a product an item can go into: the numbered line, then what
  // is made first for a step.
  const stepChoices = (pid) => {
    const b = builds[pid];
    if (!b) return [];
    const line = (b.steps || []).map((id, n) => ({ id, label: `${n + 1} · ${nameOfItem(b, id)}` }));
    const first = (b.list || []).filter(id => !(b.steps || []).includes(id) && (b.items[id]?.inputs || []).length).map(id => ({ id, label: `Made first: ${nameOfItem(b, id)}` }));
    return [...line, ...first];
  };
  // What a sub-assembly could be made from: the product's list, its name's
  // words first.
  const fromChoices = (pid) => {
    const b = builds[pid];
    return b ? (b.list || []).filter(id => id !== b.final_id).map(id => ({ id, name: nameOfItem(b, id) })) : [];
  };
  const suggestions = (pid) => {
    const mine = words(existing ? existing.name : f.name), have = new Set(w.from.map(x => String(x.input_id)));
    return fromChoices(pid).filter(c => !have.has(String(c.id)) && [...words(c.name)].some(x => mine.has(x)));
  };
  // The steps that take everything it is made from today: they can take it instead.
  const swapTargets = (pid) => {
    const b = builds[pid], ids = w.from.map(x => Number(x.input_id)).filter(Boolean);
    if (!b || !ids.length) return [];
    const mine = [...new Set([...(b.list || []), ...(b.steps || [])])];
    return mine.filter(id => (b.items[id]?.inputs || []).length && ids.every(x => b.items[id].inputs.some(e => Number(e.input_id) === x)));
  };
  // Made first from a list here; what is moulded into a part has none.
  const builtHere = built && !fromPart;
  const targets = builtHere && w.mode === 'into' && w.rows[0].product_id ? swapTargets(w.rows[0].product_id) : [];
  const swapping = builtHere && w.swap && targets.length === 1;
  const shownName = existing ? existing.name : f.name;

  // Find before adding: what the name matches in Stock and in Admin.
  const finds = useMemo(() => {
    const q = f.name.trim().toLowerCase();
    if (item || existing || q.length < 3 || !stock || !opts) return null;
    const inStock = stock.filter(i => i.name.toLowerCase().includes(q)).slice(0, 5);
    const linked = (k) => new Set(stock.filter(i => i[k] != null).map(i => String(i[k])));
    const admin = [];
    for (const [k, list] of Object.entries(LIST_OF)) {
      const have = linked(k);
      for (const r of opts[list] || []) {
        if (r.discontinued || have.has(String(r.id)) || !r.name.toLowerCase().includes(q) || String(r.id) === String(f.link_id)) continue;
        admin.push({ list, id: r.id, name: r.name, code: r.part_number || r.code || null, category: r.category || null });
      }
    }
    return { inStock, admin: admin.slice(0, 6) };
  }, [f.name, f.link_id, stock, opts, item, existing]);

  const pickAdmin = (r) => {
    setF(x => ({ ...x, name: r.name, kind: KIND_OF_LIST[r.list], link_id: String(r.id), unit: unitFor(r.list, r.category) }));
    setNewPack(null);
  };

  // What is still missing, said so Save can be pressed.
  const blockers = [];
  if (!existing && !f.name.trim()) blockers.push('Give it a name, as it is said on the floor.');
  if (!existing && linkKey && !f.link_id && !newPack) blockers.push(`${WHICH[kind].replace(' is it?', '')}: pick it${kind === 'packaging' ? ', or add a new one' : ''}.`);
  if (newPack && !newPack.name.trim()) blockers.push('Give the new Packaging item a name.');
  if (PLACED.includes(kind) && w.mode === 'into') {
    if (builtHere && !w.from.some(x => x.input_id)) blockers.push(`Say what ${shownName || 'it'} is made from.`);
    if (!swapping && w.rows.some(r => !r.step_id)) blockers.push('Pick the step that takes it, or choose "Nothing uses it" if it is only counted.');
  }
  if (kind === 'stage' && w.mode === 'line' && !w.line.after_id) blockers.push('Pick the step it comes after.');
  if (kind === 'packaging' && w.also.on && !w.also.step_id) blockers.push('Pick the step it goes in at, or untick "It also goes in during assembly".');

  const placeBody = () => {
    if (!PLACED.includes(kind) || w.mode === 'keep') return null;
    if (w.mode === 'none') return { mode: 'none' };
    if (w.mode === 'line') return { mode: 'line', product_id: w.line.product_id, after_id: Number(w.line.after_id) };
    const rows = w.rows.map((r, i) => ({ product_id: r.product_id || null, step_id: Number(i === 0 && swapping ? targets[0] : r.step_id), qty: r.qty }));
    return builtHere ? { mode: 'into', rows, from: w.from.filter(x => x.input_id).map(x => ({ input_id: Number(x.input_id), qty: x.qty })), swap: swapping } : { mode: 'into', rows };
  };
  const alsoBody = () => (kind === 'packaging' && w.also.on ? { product_id: w.also.product_id || null, step_id: Number(w.also.step_id), qty: w.also.qty } : null);

  async function save(extra = {}) {
    setSaving(true);
    setError(null);
    try {
      if (existing) {
        await api.put(`/api/v1/stock/items/${existing.id}/place`, { place: placeBody(), also: alsoBody() });
        onSaved?.(existing);
        return;
      }
      let linkId = f.link_id;
      if (newPack && !linkId) {
        const { data } = await api.post('/api/v1/packaging', { values: { name: newPack.name.trim(), category: newPack.category }, to_stock: false });
        linkId = String(data.id);
        setF(x => ({ ...x, link_id: linkId }));
        setNewPack(null);
      }
      const links = { part_id: null, product_id: null, packaging_id: null, material_id: null };
      if (linkKey) links[linkKey] = linkId || null;
      else if (item) for (const k of Object.keys(links)) links[k] = item[k] ?? null;   // a link kept from before
      const body = {
        name: f.name, kind: f.kind, unit: f.unit, pack_unit: f.pack_unit || null, pack_qty: f.pack_unit ? f.pack_qty : null,
        min_qty: f.min_qty, chamfer: f.chamfer, note: f.note, from_production: f.from_production, per_part: f.per_part,
        ...links, ...extra,
      };
      if (item) {
        const { data } = await api.put(`/api/v1/stock/items/${item.id}`, body);
        const place = placeBody(), also = alsoBody();
        if (place || also) await api.put(`/api/v1/stock/items/${item.id}/place`, { place, also });
        onSaved?.(data.item);
      } else {
        const { data } = await api.post('/api/v1/stock/items', { ...body, place: placeBody(), also: alsoBody(), on_hand: f.on_hand });
        onSaved?.(data.item);
      }
    } catch (err) {
      const d = err.response?.data || {};
      setError({ text: d.error || 'Not saved. Check the connection and try again.', open: d.existing_id || null, saved: d.item || null });
      setSaving(false);
    }
  }

  const field = { background: SUNK, border: `1px solid ${BORD}`, borderRadius: 9, color: TEXT, padding: '9px 11px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui', colorScheme: 'dark' };
  const lab = { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 };
  const row = { display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: 12 };
  const choice = (on) => ({ display: 'flex', gap: 9, alignItems: 'flex-start', background: SUNK, border: `1px solid ${on ? TEAL : BORD}`, borderRadius: 9, padding: '9px 11px', cursor: 'pointer', lineHeight: 1.4, fontSize: 13.5 });
  const small = { display: 'block', color: MUTED, fontSize: 12 };
  const linkBtn = { background: 'none', border: 'none', color: TEAL, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'system-ui' };
  const xBtn = { background: 'none', border: `1px solid ${BORD}`, color: DIM, borderRadius: 8, width: 34, height: 36, cursor: 'pointer' };

  const productSelect = (value, onChange, label = 'Product') => (
    <select value={value} onChange={onChange} style={field} aria-label={label}>
      <option value="">{opts ? 'Which product…' : 'Reading…'}</option>
      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
  const stepSelect = (pid, value, onChange) => (
    <select value={value} onChange={onChange} style={field} aria-label="Which step takes it" disabled={!pid || !builds[pid]}>
      <option value="">{!pid ? 'Pick the product first' : builds[pid] ? 'Which step takes it…' : 'Reading its steps…'}</option>
      {stepChoices(pid).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
    </select>
  );
  const sourceLine = start && !item ? (fromPart
    ? `Opened on ${start.part_name}, a moulded part: it goes into the part. Its material comes from its recipe and its packing from Admin → Packaging; this is for what is moulded into it.`
    : start.step_id ? `Opened on ${start.step_name || 'a step'} of ${start.product_name}: the product and the step are picked.`
      : `Opened in ${start.product_name}: the product is picked. Pick the step.`) : null;

  const whereBox = (
    <section style={{ border: `1px solid ${TEAL}88`, background: 'rgba(20,184,166,0.06)', borderRadius: 12, padding: '12px 12px 4px' }} aria-label="Where does it go?">
      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Where does it go?</div>
      {kind === 'packaging' && (
        <>
          <p style={{ color: DIM, fontSize: 12.5, margin: '0 0 10px', lineHeight: 1.5 }}>Packaging finds its place from <b>Admin → Packaging</b>: the packing of each product says what goes into what, and how many. So here you only say which item it is there.</p>
          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 10, cursor: 'pointer', fontSize: 13.5 }}>
            <input type="checkbox" checked={w.also.on} onChange={e => setW(x => ({ ...x, also: { ...x.also, on: e.target.checked } }))} style={{ accentColor: TEAL, marginTop: 2 }} />
            <span>It also goes in during assembly, at a step<span style={small}>Rare: like Snail Collect's zip-lock bag, which step 1 fills. Its line in the packing is then not counted twice.</span></span>
          </label>
          {w.also.on && (
            <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1.3fr 76px', gap: 6, marginBottom: 10 }}>
              {productSelect(w.also.product_id, e => setW(x => ({ ...x, also: { ...x.also, product_id: e.target.value, step_id: '' } })))}
              {stepSelect(w.also.product_id, w.also.step_id, e => setW(x => ({ ...x, also: { ...x.also, step_id: e.target.value } })))}
              <input value={w.also.qty} onChange={e => setW(x => ({ ...x, also: { ...x.also, qty: e.target.value } }))} inputMode="decimal" style={field} aria-label="How many go into one" />
            </div>
          )}
        </>
      )}
      {kind === 'material' && <p style={{ color: DIM, fontSize: 12.5, margin: '0 0 10px', lineHeight: 1.5 }}>A material's use comes from the recipes in Admin → Parts, by weight. Nothing else to say here.</p>}
      {kind === 'finished' && <p style={{ color: DIM, fontSize: 12.5, margin: '0 0 10px', lineHeight: 1.5 }}>A finished product is the top of its own BOM, so nothing takes it. What it is made of is set in its Build steps.</p>}
      {PLACED.includes(kind) && (
        <>
          {item && (
            <p style={{ color: DIM, fontSize: 12.5, margin: '0 0 10px', lineHeight: 1.5 }}>
              Now: {(item.used_in || []).length ? <>{item.used_in.map(u => u.name).join(', ')} take{item.used_in.length === 1 ? 's' : ''} it.</> : item.only_counted ? 'nothing uses it, it is only counted.' : 'nothing takes it yet.'}
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }} role="radiogroup" aria-label="Where it goes">
            {item && (
              <label style={choice(w.mode === 'keep')}><input type="radio" name="w-mode" checked={w.mode === 'keep'} onChange={() => setW(x => ({ ...x, mode: 'keep' }))} style={{ accentColor: TEAL, marginTop: 2 }} />
                <span>Leave it as it is</span></label>
            )}
            <label style={choice(w.mode === 'into')}><input type="radio" name="w-mode" checked={w.mode === 'into'} onChange={() => setW(x => ({ ...x, mode: 'into' }))} style={{ accentColor: TEAL, marginTop: 2 }} />
              <span>{fromPart ? 'Moulded into the part' : builtHere ? 'Made first, then a step takes it' : item ? 'A step takes it (as well)' : 'A step takes it'}
                <span style={small}>{fromPart ? `Each good ${start.part_name} takes it when a run ends.` : builtHere ? 'Say what it is made from; the app finds the step that takes those today.' : 'Pick the product and the step that uses it, and how many go into one.'}</span></span></label>
            {kind === 'stage' && !fromPart && (
              <label style={choice(w.mode === 'line')}><input type="radio" name="w-mode" checked={w.mode === 'line'} onChange={() => setW(x => ({ ...x, mode: 'line' }))} style={{ accentColor: TEAL, marginTop: 2 }} />
                <span>A new step in a product's line<span style={small}>It goes between two numbered steps: it takes the one before, and the next one takes it instead.</span></span></label>
            )}
            {!fromPart && (
              <label style={choice(w.mode === 'none')}><input type="radio" name="w-mode" checked={w.mode === 'none'} onChange={() => setW(x => ({ ...x, mode: 'none' }))} style={{ accentColor: TEAL, marginTop: 2 }} />
                <span>Nothing uses it, it is only counted<span style={small}>Gloves, cleaning spray, spare parts for the machines.</span></span></label>
            )}
          </div>

          {w.mode === 'into' && fromPart && (
            <label style={{ display: 'block', marginBottom: 10 }}><span style={lab}>How many in each {start.part_name}</span>
              <input value={w.rows[0].qty} onChange={e => setRow(0, { qty: e.target.value })} inputMode="decimal" style={{ ...field, maxWidth: 160 }} /></label>
          )}

          {w.mode === 'into' && builtHere && (() => {
            const pid = w.rows[0].product_id, sg = pid ? suggestions(pid) : [];
            return (
              <>
                <label style={{ display: 'block', marginBottom: 10 }}><span style={lab}>Product</span>
                  {productSelect(pid, e => { setRow(0, { product_id: e.target.value, step_id: '' }); setW(x => ({ ...x, from: [] })); })}</label>
                <span style={lab}>Made from</span>
                {sg.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', margin: '2px 0 8px', fontSize: 12, color: DIM }}>
                    Its name suggests:
                    {sg.map(s => <button key={s.id} type="button" onClick={() => setW(x => ({ ...x, from: [...x.from, { input_id: String(s.id), qty: '1' }] }))}
                      style={{ background: 'rgba(20,184,166,0.12)', border: `1px dashed ${TEAL}`, color: TEAL, borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui' }}>+ {s.name}</button>)}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 }}>
                  {w.from.map((x, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 76px 34px', gap: 6 }}>
                      <select value={x.input_id} onChange={e => setW(y => ({ ...y, from: y.from.map((z, j) => (j === i ? { ...z, input_id: e.target.value } : z)) }))} style={field} aria-label="What it is made from" disabled={!pid}>
                        <option value="">Pick…</option>
                        {fromChoices(pid).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <input value={x.qty} onChange={e => setW(y => ({ ...y, from: y.from.map((z, j) => (j === i ? { ...z, qty: e.target.value } : z)) }))} inputMode="decimal" style={field} aria-label="How many" />
                      <button type="button" onClick={() => setW(y => ({ ...y, from: y.from.filter((_, j) => j !== i) }))} style={xBtn} aria-label="Take this line off">×</button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setW(x => ({ ...x, from: [...x.from, { input_id: '', qty: '1' }] }))} style={{ ...linkBtn, marginBottom: 10 }} disabled={!pid}>+ add what it is made from</button>
                {targets.length === 1 && (
                  <label style={{ ...choice(w.swap), marginBottom: 10 }}>
                    <input type="checkbox" checked={w.swap} onChange={e => setW(x => ({ ...x, swap: e.target.checked }))} style={{ accentColor: TEAL, marginTop: 2 }} />
                    <span>{nameOfItem(builds[pid], targets[0])} takes {w.from.filter(x => x.input_id).map(x => nameOfItem(builds[pid], x.input_id)).join(' and ')} today. Should it take 1 {shownName || 'of it'} instead?
                      <span style={small}>Then it is made first for that step, and the order follows by itself.</span></span>
                  </label>
                )}
                {!swapping && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 76px', gap: 6, marginBottom: 10 }}>
                    {stepSelect(pid, w.rows[0].step_id, e => setRow(0, { step_id: e.target.value }))}
                    <input value={w.rows[0].qty} onChange={e => setRow(0, { qty: e.target.value })} inputMode="decimal" style={field} aria-label="How many go into one" />
                  </div>
                )}
              </>
            );
          })()}

          {w.mode === 'into' && !fromPart && !builtHere && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 }}>
                {w.rows.map((r, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0,1fr) 70px 34px' : 'minmax(0,1fr) minmax(0,1.3fr) 76px 34px', gap: 6 }}>
                    <div style={phone ? { gridColumn: '1 / -1' } : null}>{productSelect(r.product_id, e => setRow(i, { product_id: e.target.value, step_id: '' }))}</div>
                    {stepSelect(r.product_id, r.step_id, e => setRow(i, { step_id: e.target.value }))}
                    <input value={r.qty} onChange={e => setRow(i, { qty: e.target.value })} inputMode="decimal" style={field} aria-label="How many go into one" title="How many go into one" />
                    <button type="button" disabled={w.rows.length === 1} onClick={() => setW(x => ({ ...x, rows: x.rows.filter((_, j) => j !== i) }))} style={{ ...xBtn, opacity: w.rows.length === 1 ? 0.4 : 1 }} aria-label="Take this line off">×</button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setW(x => ({ ...x, rows: [...x.rows, { product_id: '', step_id: '', qty: '1' }] }))} style={{ ...linkBtn, marginBottom: 8 }}>+ it goes into another product too</button>
              <p style={{ color: MUTED, fontSize: 12, margin: '0 0 10px' }}>How many go into one of that step: 1 leaflet in one packed product, 1/15 for a box that holds 15.</p>
            </>
          )}

          {w.mode === 'line' && (
            <div style={{ ...row, marginBottom: 10 }}>
              <label><span style={lab}>Product</span>{productSelect(w.line.product_id, e => setW(x => ({ ...x, line: { product_id: e.target.value, after_id: '' } })))}</label>
              <label><span style={lab}>After step</span>
                <select value={w.line.after_id} onChange={e => setW(x => ({ ...x, line: { ...x.line, after_id: e.target.value } }))} style={field} disabled={!builds[w.line.product_id]} aria-label="After step">
                  <option value="">{builds[w.line.product_id] ? 'Pick a step…' : 'Pick the product first'}</option>
                  {(builds[w.line.product_id]?.steps || []).slice(0, -1).map((id, n) => <option key={id} value={id}>{n + 1} · {nameOfItem(builds[w.line.product_id], id)}</option>)}
                </select></label>
            </div>
          )}
          {w.mode !== 'keep' && w.mode !== 'none' && LEVEL_SAID[kind] && <p style={{ color: MUTED, fontSize: 12, margin: '0 0 10px' }}>In the BOM: {LEVEL_SAID[kind]}.</p>}
        </>
      )}
    </section>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1300, display: 'flex', alignItems: phone ? 'flex-end' : 'flex-start', justifyContent: 'center', padding: phone ? 0 : '5vh 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label={item ? 'Change stock item' : 'New stock item'}
        style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: phone ? '16px 16px 0 0' : 16, width: '100%', maxWidth: 640, maxHeight: phone ? '94vh' : 'none', overflowY: phone ? 'auto' : 'visible',
                 padding: phone ? '14px 16px calc(18px + env(safe-area-inset-bottom, 0px))' : '20px 22px', fontFamily: 'system-ui', color: TEXT, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ flex: 1, fontSize: 17, fontWeight: 800 }}>{existing ? `Put ${existing.name} where it goes` : item ? `Change ${item.name}` : 'New stock item'}</div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        {sourceLine && <p style={{ borderLeft: `3px solid ${TEAL}`, background: 'rgba(20,184,166,0.08)', borderRadius: '0 9px 9px 0', padding: '8px 11px', fontSize: 12.5, lineHeight: 1.5, margin: '0 0 12px' }}>{sourceLine}</p>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {existing ? (
            <p style={{ color: DIM, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
              {existing.name} is already in Stock ({kindOf(existing.kind).label}), so it is not added again: say where else it goes, and Save. Its name, count and details stay as they are.{' '}
              <button type="button" onClick={() => setExisting(null)} style={linkBtn}>It is something else</button>
            </p>
          ) : (
            <>
              {nameFollows ? (
                <div>
                  <span style={lab}>Name, as said on the floor</span>
                  <div id="stock-name" style={{ ...field, color: DIM }}>{f.name}</div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 5, lineHeight: 1.45 }}>It comes from {NAME_HOME[kind]}. To change it, set its Floor name there, and it changes here too.</div>
                </div>
              ) : (
                <label><span style={lab}>Name, as said on the floor</span>
                  <input id="stock-name" value={f.name} onChange={set('name')} style={field} placeholder="e.g. Cevka Ø5 x 215 Zelena" autoComplete="off" /></label>
              )}
              {finds && (finds.inStock.length > 0 || finds.admin.length > 0) && (
                <div style={{ border: `1px solid ${BORD}`, borderRadius: 10, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, marginTop: -4 }}>
                  {finds.inStock.length > 0 && <div style={{ ...lab, marginBottom: 0 }}>Already in Stock</div>}
                  {finds.inStock.map(i => (
                    <div key={`s${i.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, flexWrap: 'wrap' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>{i.name} <span style={{ color: kindOf(i.kind).color, fontSize: 12 }}>{kindOf(i.kind).label}</span></span>
                      {PLACED.includes(i.kind) && <button type="button" onClick={() => { setExisting(i); setW(x => ({ ...x, mode: 'into' })); }} style={linkBtn}>Use it here</button>}
                      {onOpenItem && <button type="button" onClick={() => onOpenItem(i.id)} style={{ ...linkBtn, color: DIM }}>Open it</button>}
                    </div>
                  ))}
                  {finds.admin.length > 0 && <div style={{ ...lab, marginBottom: 0, marginTop: finds.inStock.length ? 4 : 0 }}>In Admin, not in Stock yet</div>}
                  {finds.admin.map(r => (
                    <div key={`a${r.list}${r.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, flexWrap: 'wrap' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>{r.name}{r.code && <span style={{ color: MUTED, fontSize: 11.5, marginLeft: 6 }}>{r.code}</span>} <span style={{ color: kindOf(KIND_OF_LIST[r.list]).color, fontSize: 12 }}>{kindOf(KIND_OF_LIST[r.list]).label}</span></span>
                      <button type="button" onClick={() => pickAdmin(r)} style={linkBtn}>Add it</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={row}>
                <label><span style={lab}>Kind</span>
                  <select id="stock-kind" value={f.kind} onChange={e => { const k = e.target.value; setF(x => ({ ...x, kind: k, link_id: '' })); setNewPack(null); setW(x => ({ ...x, mode: item ? 'keep' : x.mode === 'line' && k !== 'stage' ? 'into' : x.mode })); }} style={field}>
                    {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                  </select></label>
                <label><span style={lab}>Counted in</span>
                  <input id="stock-unit" value={f.unit} onChange={set('unit')} list="stock-units" style={field} />
                  <datalist id="stock-units"><option value="kos" /><option value="m" /><option value="kg" /></datalist></label>
              </div>

              {linkKey && (
                <div>
                  <label><span style={lab}>{WHICH[kind]}</span>
                    <select id="stock-link-id" value={String(f.link_id)} onChange={e => { const v = e.target.value; const r = (opts?.[LIST_OF[linkKey]] || []).find(o => String(o.id) === v); setF(x => ({ ...x, link_id: v, unit: !item && r ? unitFor(LIST_OF[linkKey], r.category) : x.unit, name: r && NAME_HOME[kind] && !familyPiece(v) ? (r.floor_name || r.name) : (x.name || (r ? r.name : '')) })); }} style={field} disabled={!opts || !!newPack}>
                      <option value="">{opts ? 'Pick it…' : 'Reading…'}</option>
                      {(opts?.[LIST_OF[linkKey]] || []).filter(o => !o.discontinued || String(o.id) === String(f.link_id)).map(o => <option key={o.id} value={String(o.id)}>{o.name}{o.part_number ? ` (${o.part_number})` : ''}</option>)}
                    </select></label>
                  {kind === 'packaging' && !item && !f.link_id && (newPack ? (
                    <div style={{ ...row, marginTop: 8 }}>
                      <label><span style={lab}>New Packaging item's name</span><input value={newPack.name} onChange={e => setNewPack(p => ({ ...p, name: e.target.value }))} style={field} /></label>
                      <label><span style={lab}>What kind of packaging</span>
                        <select value={newPack.category} onChange={e => setNewPack(p => ({ ...p, category: e.target.value }))} style={field}>
                          {PACK_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select></label>
                      <span style={{ color: MUTED, fontSize: 12, gridColumn: '1 / -1' }}>Save adds it to Admin → Packaging too; its sizes and weight are filled in there. <button type="button" onClick={() => setNewPack(null)} style={linkBtn}>Pick an existing one instead</button></span>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setNewPack({ name: f.name, category: 'other' })} style={{ ...linkBtn, marginTop: 6 }}>Not in Admin → Packaging yet? Add it from here</button>
                  ))}
                </div>
              )}

              {kind === 'moulded' && (
                <div style={row}>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input id="stock-from-production" type="checkbox" checked={f.from_production} onChange={set('from_production')} style={{ width: 20, height: 20, accentColor: TEAL, marginTop: 1 }} />
                    <span style={{ fontSize: 13.5, lineHeight: 1.4 }}><b>Production adds to it</b><br /><span style={{ color: DIM, fontSize: 12.5 }}>Good parts recorded when a run ends on Today go into its stock. Left off for a part sold as it is (on no product's list), runs go into its stock once Automatic stock is switched on.</span></span>
                  </label>
                  <label><span style={lab}>{f.unit || 'units'} per good part counted</span>
                    <input id="stock-per-part" value={f.per_part} onChange={set('per_part')} inputMode="decimal" disabled={!f.from_production}
                      placeholder="1, or 1/3 if Today counts every piece of a three-piece mould" style={{ ...field, opacity: f.from_production ? 1 : 0.5 }} /></label>
                </div>
              )}

              <div style={row}>
                <label><span style={lab}>Pack (optional)</span>
                  <input id="stock-pack-unit" value={f.pack_unit} onChange={set('pack_unit')} placeholder="e.g. bala, box" style={field} /></label>
                <label><span style={lab}>{f.unit || 'units'} in one {f.pack_unit || 'pack'}</span>
                  <input id="stock-pack-qty" value={f.pack_qty} onChange={set('pack_qty')} inputMode="decimal" placeholder="e.g. 50" disabled={!f.pack_unit} style={{ ...field, opacity: f.pack_unit ? 1 : 0.5 }} /></label>
              </div>

              <div style={row}>
                <label><span style={lab}>Minimum ({f.unit || 'units'}, optional)</span>
                  <input id="stock-min" value={f.min_qty} onChange={set('min_qty')} inputMode="decimal" placeholder="Red in the list below this" style={field} /></label>
                {!item ? (
                  <label><span style={lab}>On hand now ({f.unit || 'units'}, optional)</span>
                    <input id="stock-on-hand" value={f.on_hand} onChange={set('on_hand')} inputMode="decimal" placeholder="Counted as it is added" style={field} /></label>
                ) : (
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingTop: phone ? 0 : 20, cursor: 'pointer' }}>
                    <input id="stock-chamfer" type="checkbox" checked={f.chamfer} onChange={set('chamfer')} style={{ width: 20, height: 20, accentColor: TEAL, marginTop: 1 }} />
                    <span style={{ fontSize: 13.5, lineHeight: 1.4 }}><b>Pofrezkane</b><br /><span style={{ color: DIM, fontSize: 12.5 }}>A hard tube: its stock is kept as pofrezkane and not</span></span>
                  </label>
                )}
              </div>
            </>
          )}

          {whereBox}

          {!existing && (
            <label><span style={lab}>Note (optional)</span>
              <textarea id="stock-note" value={f.note} onChange={set('note')} rows={2} style={{ ...field, resize: 'vertical' }} /></label>
          )}
        </div>

        {blockers.length > 0 && (
          <ul style={{ color: AMBER, fontSize: 12.5, margin: '12px 0 0', paddingLeft: 18, lineHeight: 1.55 }}>{blockers.map(b => <li key={b}>{b}</li>)}</ul>
        )}
        {error && (
          <div style={{ color: RED, fontSize: 13, marginTop: 12, lineHeight: 1.5 }}>
            {error.text}
            {error.open && onOpenItem && <> <button type="button" onClick={() => onOpenItem(error.open)} style={linkBtn}>Open it</button></>}
            {error.saved && <> <button type="button" onClick={() => onSaved?.(error.saved)} style={linkBtn}>Go to it</button></>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {item && (item.active !== false
            ? <button type="button" disabled={saving} onClick={() => save({ active: false })} title="Off the list, with its history kept. It can be brought back."
                style={{ background: 'none', border: `1px solid ${AMBER}66`, color: AMBER, borderRadius: 9, padding: '9px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'system-ui' }}>Retire it</button>
            : <button type="button" disabled={saving} onClick={() => save({ active: true })}
                style={{ background: 'none', border: `1px solid ${TEAL}66`, color: TEAL, borderRadius: 9, padding: '9px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'system-ui' }}>Bring it back</button>)}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={{ background: 'none', border: '1px solid #374151', color: DIM, borderRadius: 9, padding: '9px 16px', fontSize: 13.5, cursor: 'pointer', fontFamily: 'system-ui' }}>Cancel</button>
          <button type="button" onClick={() => save()} disabled={saving || blockers.length > 0}
            style={{ background: TEAL, border: 'none', color: '#0f172a', borderRadius: 9, padding: '9px 18px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'system-ui', opacity: saving || blockers.length ? 0.5 : 1 }}>
            {saving ? 'Saving…' : existing ? 'Save where it goes' : item ? 'Save' : 'Add it'}
          </button>
        </div>
      </div>
    </div>
  );
}
